import { useState } from 'react';
import { addComment } from '../../api/endpoints';
import { ApiError, toApiError } from '../../api/errors';
import type { Id, TicketComment } from '../../api/types';
import { formatRelative } from '../../lib/format';
import { Avatar } from '../ui/Avatar';
import { Spinner } from '../ui/Skeleton';

/**
 * Comments. The body is rendered as PLAIN TEXT with whitespace preserved.
 *
 * ⛔ No markdown-to-HTML, no `dangerouslySetInnerHTML`. A comment is user-supplied, and the moment
 * it becomes markup it becomes a script-injection surface — the sanitiser then has to be right
 * forever, and being wrong once is stored XSS on every reader of that ticket. Images live in
 * `ticket_link` (db/007) and are rendered by LinkList, which is the deliberate, narrow exception.
 */
export function CommentList({
  ticketId,
  comments,
  onAdded,
}: {
  ticketId: Id;
  comments: TicketComment[];
  onAdded: () => void;
}) {
  return (
    <section aria-labelledby="comments-heading" className="space-y-3">
      <h3 id="comments-heading" className="text-sm font-semibold">
        Comments <span className="text-[color:var(--color-ink-muted)]">({comments.length})</span>
      </h3>

      <CommentComposer ticketId={ticketId} onAdded={onAdded} />

      {comments.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">No comments yet.</p>
      ) : (
        <ul className="space-y-4">
          {comments.map((comment) => (
            <li key={comment.id} className="flex gap-2.5">
              {/* `author` is the GitLab username the API stored (its `userHandle`). There is no
                  display name or avatar on the comment endpoint, so initials it is. */}
              <Avatar name={comment.author} size={26} />
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className="font-semibold text-[color:var(--color-ink)]">{comment.author}</span>
                  <span className="text-[color:var(--color-ink-muted)]">
                    {formatRelative(comment.created_at)}
                  </span>
                </p>
                <p className="mt-1 text-sm whitespace-pre-wrap text-[color:var(--color-ink)]">
                  {comment.body}
                </p>
                {/* ⚠️ `ticket_link.comment_id` exists in db/007 but GET /tickets/:id/links returns
                    only ticket-level links, so per-comment images cannot be shown yet. Recorded as
                    an API gap rather than rendered as "this comment has no images". */}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CommentComposer({ ticketId, onAdded }: { ticketId: Id; onAdded: () => void }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await addComment(ticketId, trimmed);
      setBody('');
      setBusy(false);
      onAdded();
    } catch (err) {
      setBusy(false);
      // ⛔ The draft is NOT cleared on failure — losing what someone typed is the worst possible
      // response to a network error.
      setError(toApiError(err, `/tickets/${ticketId}/comments`));
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      className="space-y-2"
    >
      <label className="sr-only" htmlFor={`comment-${ticketId}`}>
        Add a comment
      </label>
      <textarea
        id={`comment-${ticketId}`}
        rows={2}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Add a comment…"
        className="w-full resize-y rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2.5 py-2 text-sm"
      />
      {error ? (
        <p className="text-xs text-red-700 dark:text-red-300">
          <span className="font-semibold">{error.message}</span>
          {error.reason ? ` — ${error.reason}` : ''} Your text has been kept.
        </p>
      ) : null}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy || body.trim() === ''}
          className="inline-flex h-9 items-center gap-2 rounded bg-[color:var(--color-accent)] px-3 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
        >
          {busy ? <Spinner label="Saving comment" /> : null}
          Comment
        </button>
      </div>
    </form>
  );
}
